/**
 * Basis-Service für API-Aufrufe
 * Stellt grundlegende Methoden für alle Service-Klassen bereit
 */

import axios from 'axios';
import ErrorHandler from '../utils/errorHandler';
import { AURA_CLIENT_HEADERS } from '../utils/auraClient';

// Shared state für Token-Refresh across all ApiService instances
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

class ApiService {
  constructor() {
    // KRITISCHER FIX: Verwende REACT_APP_BACKEND_URL statt REACT_APP_API_URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002';
    this.baseURL = `${backendUrl}/api/v1`;
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        ...AURA_CLIENT_HEADERS
      }
    });
    
    // Request Interceptor - HARMONISIERT mit authorizedFetch
    this.api.interceptors.request.use(
      (config) => {
        // Auth-Token hinzufügen, wenn vorhanden
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Tenant-Header: schoolId aus localStorage als Hinweis mitsenden
        // (Backend bevorzugt für authentifizierte User den JWT-Wert, Header nur als Fallback für SUPERADMIN)
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const schoolId = user.schoolId || localStorage.getItem('schoolId');

        if (schoolId) {
          config.headers['X-School-ID'] = schoolId;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 🔧 CRITICAL FIX: Response Interceptor mit automatischem Token-Refresh
    this.setupTokenRefreshInterceptor();
  }

  /**
   * Setup Response Interceptor für automatischen Token-Refresh
   * Verhindert 401-Fehler durch automatische Token-Erneuerung
   * Nutzt globale Variablen für Synchronisation across instances
   */
  setupTokenRefreshInterceptor() {
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Spezielle Behandlung für Authentifizierungsfehler (401)
        if (error.response?.status === 401 && !originalRequest?._retry) {
          // Nicht bei Login/Register/Refresh Routen
          const isAuthRoute = originalRequest?.url?.includes('/auth/login')
                           || originalRequest?.url?.includes('/auth/register')
                           || originalRequest?.url?.includes('/auth/refresh');

          if (isAuthRoute) {
            return Promise.reject(error);
          }

          console.warn('🔄 401 in ApiService - versuche Token-Refresh', {
            url: originalRequest?.url
          });

          // Wenn bereits ein Refresh läuft, warte auf das Ergebnis
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.api(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const token = localStorage.getItem('token');

          if (!token) {
            isRefreshing = false;
            processQueue(new Error('Kein Token vorhanden'), null);
            return Promise.reject(error);
          }

          try {
            // Versuche Token zu erneuern
            const response = await axios.post(
              `${this.baseURL}/auth/refresh`,
              { token },
              {
                headers: { 'Content-Type': 'application/json', ...AURA_CLIENT_HEADERS },
                timeout: 10000
              }
            );

            if (response.data?.token) {
              const newToken = response.data.token;

              // Speichere neues Token
              localStorage.setItem('token', newToken);
              localStorage.setItem('token_set_time', Date.now().toString());

              console.log('✅ Token erfolgreich erneuert in ApiService');

              // Aktualisiere Authorization Header
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              // Verarbeite wartende Requests
              processQueue(null, newToken);
              isRefreshing = false;

              // Wiederhole den ursprünglichen Request
              return this.api(originalRequest);
            } else {
              // 🔧 BUGFIX: Leere Response - State zuruecksetzen
              console.error('❌ Token-Refresh: Leere Response (kein token in response.data)');
              isRefreshing = false;
              processQueue(new Error('Leere Refresh-Response'), null);

              // Token entfernen und ausloggen
              localStorage.removeItem('token');
              const authErrorEvent = new CustomEvent('auth:error', {
                detail: {
                  message: 'Sitzung konnte nicht erneuert werden. Bitte melde dich erneut an.',
                  source: 'ApiService',
                  url: originalRequest?.url
                }
              });
              window.dispatchEvent(authErrorEvent);

              return Promise.reject(new Error('Leere Refresh-Response'));
            }
          } catch (refreshError) {
            console.error('❌ Token-Refresh fehlgeschlagen:', refreshError.message);

            // Refresh fehlgeschlagen - Token entfernen
            localStorage.removeItem('token');
            processQueue(refreshError, null);
            isRefreshing = false;

            // Globales Auth-Error-Event auslösen
            const authErrorEvent = new CustomEvent('auth:error', {
              detail: {
                message: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
                source: 'ApiService',
                url: originalRequest?.url
              }
            });
            window.dispatchEvent(authErrorEvent);

            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }
  
  /**
   * Führt einen GET-Request aus
   * @param {string} url - API-Endpunkt
   * @param {Object} params - Query-Parameter
   * @returns {Promise<Object>} - API-Antwort
   */
  async get(url, params = {}) {
    try {
      const response = await this.api.get(url, { params });
      return response.data;
    } catch (error) {
      // HARMONISIERT: Verwende einheitlichen ErrorHandler
      ErrorHandler.handle(error);
      throw error;
    }
  }
  
  /**
   * Führt einen POST-Request aus
   * @param {string} url - API-Endpunkt
   * @param {Object} data - Zu sendende Daten
   * @returns {Promise<Object>} - API-Antwort
   */
  async post(url, data = {}) {
    try {
      const response = await this.api.post(url, data);
      return response.data;
    } catch (error) {
      // HARMONISIERT: Verwende einheitlichen ErrorHandler
      ErrorHandler.handle(error);
      throw error;
    }
  }
  
  /**
   * Führt einen PUT-Request aus
   * @param {string} url - API-Endpunkt
   * @param {Object} data - Zu sendende Daten
   * @returns {Promise<Object>} - API-Antwort
   */
  async put(url, data = {}) {
    try {
      const response = await this.api.put(url, data);
      return response.data;
    } catch (error) {
      // HARMONISIERT: Verwende einheitlichen ErrorHandler
      ErrorHandler.handle(error);
      throw error;
    }
  }
  
  /**
   * Führt einen PATCH-Request aus
   * @param {string} url - API-Endpunkt
   * @param {Object} data - Zu sendende Daten
   * @returns {Promise<Object>} - API-Antwort
   */
  async patch(url, data = {}) {
    try {
      const response = await this.api.patch(url, data);
      return response.data;
    } catch (error) {
      // HARMONISIERT: Verwende einheitlichen ErrorHandler
      ErrorHandler.handle(error);
      throw error;
    }
  }

  /**
   * Führt einen DELETE-Request aus
   * @param {string} url - API-Endpunkt
   * @returns {Promise<Object>} - API-Antwort
   */
  async delete(url) {
    try {
      const response = await this.api.delete(url);
      return response.data;
    } catch (error) {
      // HARMONISIERT: Verwende einheitlichen ErrorHandler
      ErrorHandler.handle(error);
      throw error;
    }
  }
  
  /**
   * Hilfsmethode für sicheren API-Aufruf mit Fallback auf Mock-Daten
   * @param {Function} apiCall - Die auszuführende API-Funktion
   * @param {Object} mockData - Beispieldaten für Demo-Benutzer oder bei Fehlern
   * @returns {Promise<Object>} - Die API-Antwort oder Mock-Daten
   */
  /**
   * STUFE 2: Get Top Matches für Infinite Scroll
   * @param {number} page - Seitenzahl
   * @param {number} limit - Anzahl Profile pro Seite
   * @param {Object} filters - Optionale Filter
   * @returns {Promise<Object>} - Top Matches mit Pagination
   */
  async getTopMatches(page = 1, limit = 20, filters = {}) {
    try {
      console.log(`🎯 STUFE 2: API Call - getTopMatches(page=${page}, limit=${limit})`);
      
      // Verwende die Profile-Route mit discover endpoint
      const response = await this.api.get('/profile/discover', {
        params: {
          page,
          limit,
          ...filters
        }
      });
      
      console.log('✅ getTopMatches API Response:', response.data);
      console.log('🔍 DEBUG Response Structure:', {
        hasSuccess: !!response.data.success,
        hasData: !!response.data.data,
        dataKeys: response.data.data ? Object.keys(response.data.data) : 'no data',
        profileCount: response.data.data?.profiles?.length || 'no profiles'
      });
      
      let result;
      // Normalisiere die Response-Struktur
      if (response.data.success && response.data.data) {
        console.log('✅ Using success/data structure');
        result = {
          profiles: response.data.data.profiles || [],
          hasMore: response.data.data.pagination?.hasMore || false,
          page: response.data.data.pagination?.page || page,
          totalCount: response.data.data.pagination?.totalCount || 0,
          cached: response.data.data.meta?.cached || false
        };
      } else {
        console.log('⚠️ Using fallback structure');
        // Fallback für direkte Response
        result = {
          profiles: response.data.profiles || [],
          hasMore: response.data.hasMore || false,
          page: response.data.page || page,
          totalCount: response.data.totalCount || 0,
          cached: response.data.cached || false
        };
      }
      
      console.log('🎯 Final Result:', {
        profileCount: result.profiles.length,
        hasMore: result.hasMore,
        page: result.page,
        totalCount: result.totalCount
      });

      return result;
    } catch (error) {
      console.error('❌ getTopMatches API Error:', error);
      throw error;
    }
  }

  /**
   * STUFE 1: Smart Search API
   * @param {string} searchTerm - Suchbegriff
   * @param {Object} filters - Suchfilter
   * @param {number} page - Seitenzahl
   * @param {number} limit - Ergebnisse pro Seite
   * @returns {Promise<Object>} - Suchergebnisse
   */
  async searchProfiles(searchTerm, filters = {}, page = 1, limit = 20) {
    // Bewusst direkt ueber this.api statt this.post (Okt 2026): this.post meldet
    // jeden Fehler per ErrorHandler-Toast. Bei der Suche kam dann "SEARCH_UNAVAILABLE"
    // bzw. "Deine Änderung wurde nicht gespeichert" als Toast, obwohl AdvancedSearch
    // den Fehler selbst ruhig im Inhalt zeigt. Der Token-Refresh (Interceptor) greift
    // trotzdem.
    const response = await this.api.post('/profile/search', {
      searchTerm: searchTerm.trim(),
      filters,
      page,
      limit
    });
    const data = response?.data;

    // Altes Backend: Serverfehler kam als HTTP 200 mit success:false und leerer
    // Liste. Das ist kein leeres Ergebnis, sondern ein Fehler.
    if (!data || typeof data !== 'object' || data.success === false) {
      const error = new Error('Suche nicht verfügbar');
      error.code = (data && typeof data.error === 'string' && data.error) || 'SEARCH_UNAVAILABLE';
      throw error;
    }

    return data;
  }

  async safeApiCall(apiCall, mockData) {
    try {
      // Prüfe, ob es sich um eine Demo-Domain handelt
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const isDemoUser = user?.email?.endsWith('@eachotter-demo.de');
      
      // Bei Demo-Nutzern direkt die Mock-Daten zurückgeben
      if (isDemoUser) {
        console.log('Demo-Benutzer erkannt, verwende Mock-Daten');
        // Kurze Verzögerung für natürlicheres Verhalten
        await new Promise(resolve => setTimeout(resolve, 800));
        return mockData;
      }
      
      // Echter API-Aufruf für reguläre Benutzer
      const response = await apiCall();
      return response;
    } catch (error) {
      // HARMONISIERT: Verwende einheitlichen ErrorHandler für bessere UX
      ErrorHandler.handle(error);
      // Bei Fehlern Mock-Daten zurückgeben, um UI-Fehler zu vermeiden
      return mockData;
    }
  }
}

export default ApiService; 